/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';

import {
  DATE_FORMAT,
  OPTIONS_SEXE_ETCV_AVEC_INDETERMINE,
  estSansComplement,
  libelleComplementActe,
} from '@/constants';
import { parseSexe } from './payloads';

/**
 * Helpers de LECTURE → AFFICHAGE, pour les fiches et les listes.
 *
 * Pendant de `payloads/formatters.ts` (formulaire → API) et de
 * `payloads/parsers.ts` (API → formulaire).
 *
 * Invariant de tout le module : **une donnée absente rend une chaîne vide**.
 * Jamais `'Invalid Date'`, `'undefined'` ni `'NaN'` à l'écran. C'est nécessaire
 * parce que l'API renvoie `''` (et non `null`) pour une date absente, et omet
 * purement les clés nulles — les deux cas se présentent donc.
 */

/** Vide au sens affichage : `null`, `undefined`, ou chaîne blanche. */
const estVide = (valeur: unknown): boolean =>
  _.isNil(valeur) || (_.isString(valeur) && valeur.trim() === '');

/**
 * Vide au sens d'un NUMÉRO : en plus de `estVide`, un `0` compte comme non
 * renseigné. Les colonnes `*_a_ord` / `*_a_num_cpl` sont des entiers que la base
 * historique remplit à `0` plutôt qu'à NULL, et « Ordre 0 » n'a aucun sens.
 */
const numeroAbsent = (valeur: unknown): boolean =>
  estVide(valeur) || Number(valeur) === 0;

/** Composant horaire sur 2 chiffres ; `'00'` si absent. */
const deuxChiffres = (valeur?: number | string | null): string =>
  String(estVide(valeur) ? 0 : valeur).padStart(2, '0');

/** Assemble des fragments en ignorant les vides. */
const joindre = (
  fragments: Array<string | undefined>,
  separateur = ' ',
): string =>
  fragments.filter((fragment) => !estVide(fragment)).join(separateur);

/** `'2024-03-04'` → `'04/03/2024'` ; `''` si absente ou invalide. */
export const formatDateSeule = (date?: string | null): string => {
  if (estVide(date)) {
    return '';
  }

  const jour = dayjs(date as string);

  return jour.isValid() ? jour.format(DATE_FORMAT) : '';
};

/**
 * `'04/03/2024 à 14:33'`. Les heures/minutes absentes valent `00`
 * (comportement historique de la fiche), mais une date absente rend `''` —
 * plutôt que l'ancien `'Invalid Date à 00:00'`.
 *
 * `heure` peut être une chaîne : `ai_nai_heure` est casté `string` côté API.
 */
export const formatDateHeure = (
  date?: string | null,
  heure?: number | string | null,
  minute?: number | string | null,
): string => {
  const jour = formatDateSeule(date);

  if (jour === '') {
    return '';
  }

  return `${jour} à ${deuxChiffres(heure)}:${deuxChiffres(minute)}`;
};

/**
 * `'14:00'`, `''` si l'heure est absente. Une minute absente vaut `00`, mais
 * une heure à `0` est minuit : l'API renvoie des entiers, `0` est une valeur.
 */
export const formatHeure = (
  heure?: number | string | null,
  minute?: number | string | null,
): string =>
  estVide(heure) ? '' : `${deuxChiffres(heure)}:${deuxChiffres(minute)}`;

/**
 * Variante des listes : `'04/03/2024 à 14h33'`, et `'-'` si la date est absente.
 *
 * Trois écarts volontaires avec `formatDateHeure`, qui reproduisent le rendu
 * historique des listes : séparateur `h` plutôt que `:`, heure **non complétée**
 * à deux chiffres (`9h05`), et heure entièrement omise si elle est absente
 * (là où la fiche affiche `00:00`).
 */
export const formatDateHeureListe = (
  date?: string | null,
  heure?: number | string | null,
  minute?: number | string | null,
): string => {
  const jour = formatDateSeule(date);

  if (jour === '') {
    return '-';
  }

  if (estVide(heure)) {
    return jour;
  }

  return `${jour} à ${heure}h${deuxChiffres(minute)}`;
};

/** `'51 (Marne)'`, ou le code seul si le libellé manque, ou `''`. */
export const formatDepartement = (
  code?: string | null,
  libelle?: string | null,
): string => {
  if (estVide(code)) {
    return '';
  }

  return estVide(libelle) ? String(code) : `${code} (${libelle})`;
};

/** `'51000 Châlons-en-Champagne (FRANCE)'`, en omettant les parties absentes. */
export const formatCommunePays = (
  codePostal?: string | null,
  localite?: string | null,
  pays?: string | null,
): string => {
  const commune = joindre([codePostal as string, localite as string]);
  const paysAffiche = estVide(pays) ? '' : `(${pays})`;

  return joindre([commune, paysAffiche]);
};

/**
 * Adresse d'un individu sous un préfixe (`deces`, `domicile`), en lignes, sur
 * le modèle demandé par le métier (cf. la résidence commune des PACS) :
 *
 * ```
 * 12 bis Avenue de la Gare
 * ZAC les Bords de Reppe
 * 83190 - Ollioules (FRANCE)
 * ```
 *
 * Les lignes vides sont omises. Le n° de voirie `0`, qu'ETCV écrit faute de
 * numéro, n'est pas affiché.
 */
export const formatAdresseLignes = (
  individu: Record<string, any> | undefined,
  prefixe: string,
): string[] => {
  const champ = (suffixe: string) => individu?.[`${prefixe}${suffixe}`];
  const numero = champ('NumeroVoirie');

  const voie = joindre([
    numeroAbsent(numero) ? undefined : String(numero),
    champ('ComplementNumeroVoirie'),
    champ('LibelleVoie'),
  ]);

  const complements = [
    champ('ComplementIdentification'),
    champ('ComplementGeographique'),
    champ('ComplementDistribution'),
  ];

  const commune = joindre([champ('CodePostal'), champ('Localite')], ' - ');
  const pays = estVide(champ('Pays')) ? '' : `(${champ('Pays')})`;

  return [voie, ...complements, joindre([commune, pays])]
    .map((ligne) => (estVide(ligne) ? '' : String(ligne).trim()))
    .filter(Boolean);
};

/**
 * `'Année 2019 - Registre des naissances'`.
 *
 * ⚠️ L'année vient en priorité de `registre.annee.annee` (la relation vers
 * `ec_annee`), et non de `anneeActe` : c'est ce que la fiche de naissance
 * affiche historiquement, et les deux valeurs diffèrent. Le libellé retombe sur
 * `libelleRegistre` puis sur le numéro, car `rg_libreg` peut être vide en base.
 */
/**
 * Sexe depuis la LETTRE stockée en base (`M` / `F` / `I`).
 *
 * Distinct de `sexeAffiche`, qui lit le couple `sexe`/`sexeCode` d'un
 * individu : l'ex-conjoint n'est pas une ligne d'`ec_acte_individu` mais trois
 * colonnes de l'acte.
 */
export const sexeDepuisLettre = (lettre?: string | null): string =>
  ({ M: 'Masculin', F: 'Féminin', I: 'Indéterminé' })[
    (lettre ?? '').trim().toUpperCase()
  ] ?? '';

export const formatRegistreActe = (acte?: Record<string, any>): string => {
  const annee = acte?.registre?.annee?.annee ?? acte?.anneeActe;
  const libelle = estVide(acte?.registre?.libelle)
    ? acte?.libelleRegistre
    : acte?.registre?.libelle;
  const libelleAffiche =
    estVide(libelle) && !estVide(acte?.anneeRegistre)
      ? `Registre n°${acte?.anneeRegistre}`
      : libelle;

  return joindre(
    [estVide(annee) ? undefined : `Année ${annee}`, libelleAffiche],
    ' - ',
  );
};

/**
 * `'Masculin'` / `'Féminin'` / `'Indéterminé'`, mêmes libellés que le champ de
 * saisie, et `''` quand le sexe n'est pas renseigné (parent décédé ou
 * inconnu) : « Indéterminé » n'est affiché que s'il a été saisi (AD-12924).
 */
export const sexeAffiche = (individu?: Record<string, any>): string => {
  const sexe = parseSexe(individu);

  return (
    OPTIONS_SEXE_ETCV_AVEC_INDETERMINE.find(({ value }) => value === sexe)
      ?.text ?? ''
  );
};

/** `'DUPONT (MARTIN)'` — le nom d'usage entre parenthèses s'il existe. */
export const nomAffiche = (individu?: Record<string, any>): string =>
  joindre([
    individu?.nomFamille,
    estVide(individu?.nomUsage) ? undefined : `(${individu?.nomUsage})`,
  ]);

/**
 * `'DUPONT Léa'` — nom de classement, à la convention de l'API
 * (`App\\Utils\\NomClassement`) : nom en capitales puis prénom usuel seul.
 *
 * Calculé depuis l'individu plutôt que lu sur l'acte : après une modification
 * depuis la fiche, l'individu est à jour tout de suite, l'acte seulement au
 * prochain chargement (AD-12893). Chaîne vide sans nom de famille.
 */
export const nomClassement = (individu?: Record<string, any>): string =>
  estVide(individu?.nomFamille)
    ? ''
    : joindre([_.toUpper(individu?.nomFamille), individu?.prenom]);

/**
 * `'Léa, Marie, Jeanne'` — prénom usuel puis prénoms complémentaires, séparés
 * par une virgule comme dans l'ETCV (AD-12864).
 *
 * Les complémentaires sont repris tels quels : 360 les enregistre déjà séparés
 * par des virgules (`'Marie, Jeanne'`), et l'historique le plus souvent par des
 * espaces (`'Marie Josephine'`), où rien ne distingue deux prénoms d'un prénom
 * composé. Seule la virgule qui suit le prénom usuel est ajoutée.
 */
export const prenomsAffiches = (individu?: Record<string, any>): string =>
  joindre(
    [_.trim(individu?.prenom), _.trim(individu?.prenomsComplementaires)],
    ', ',
  );

/**
 * Colonne « N° acte et ordre » des listes :
 * `'Acte 42 - Ordre 7 - Complément 3'`.
 *
 * Chaque segment est omis s'il n'est pas renseigné — `null`, `''` **ou `0`**
 * (cf. `numeroAbsent`) : ni l'ordre ni le complément ne sont obligatoires, et
 * l'ancien rendu affichait `'Acte 42 - Ordre -'` pour un ordre absent.
 *
 * Rend `'-'` quand rien n'est renseigné, pour rester homogène avec les autres
 * cellules de ces listes (qui n'appliquent pas l'invariant `''` du module).
 */
export const formatNumeroActeListe = (item?: Record<string, any>): string => {
  const segments = joindre(
    [
      numeroAbsent(item?.numero) ? undefined : `Acte ${item?.numero}`,
      numeroAbsent(item?.numero_ordre)
        ? undefined
        : `Ordre ${item?.numero_ordre}`,
      estSansComplement(item?.numero_complement)
        ? undefined
        : `Complément ${libelleComplementActe(item?.numero_complement)}`,
    ],
    ' - ',
  );

  return segments || '-';
};
